// Per-category field visibility for the Case Info tab
// Determines which dynamic fields appear after case type selection
//
// Verified against LegalConnect UI via browser console extraction
// (planning/efm-extraction/03-extract-case-field-metadata.js).
//
// Two clean groups:
//   Group A (JA + Incident Zip): AP, LC, CU, SC
//   Group B (Court Jurisdiction): 21, 12, 14, 4, 13
//
// "Court Jurisdiction" is a courthouse location picker populated dynamically
// by the EFM via GetEFileLocationsECF. Different categories show different
// courthouse lists. Categories in Group A use a single fixed location ('LA').
const DEFAULT_VISIBILITY = {
    showJurisdictionalAmount: false,
    showCourtJurisdiction: false,
    showIncidentZip: true,
    showAdditionalCaseInfo: true,
};
/**
 * Per-category field visibility for LA County.
 *
 * Key = EFM category code (matches CaseCategoryOption.efmCode).
 * Verified against LegalConnect UI on 2026-02-21.
 */
export const LA_COUNTY_CATEGORY_FIELDS = {
    // --- Group A: Jurisdictional Amount + Incident Zip ---
    // Appellate
    'AP': {
        showJurisdictionalAmount: true,
        showCourtJurisdiction: false,
        showIncidentZip: true,
        showAdditionalCaseInfo: true,
    },
    // Civil Limited
    'LC': {
        showJurisdictionalAmount: true,
        showCourtJurisdiction: false,
        showIncidentZip: true,
        showAdditionalCaseInfo: true,
    },
    // Civil Unlimited
    'CU': {
        showJurisdictionalAmount: true,
        showCourtJurisdiction: false,
        showIncidentZip: true,
        showAdditionalCaseInfo: true,
    },
    // Small Claims
    'SC': {
        showJurisdictionalAmount: true,
        showCourtJurisdiction: false,
        showIncidentZip: true,
        showAdditionalCaseInfo: false,
    },
    // --- Group B: Court Jurisdiction (courthouse picker) ---
    // Civil Restraining Order
    '21': {
        showJurisdictionalAmount: false,
        showCourtJurisdiction: true,
        showIncidentZip: false,
        showAdditionalCaseInfo: false,
    },
    // Family (+ Family - DCSS, same EFM code)
    '12': {
        showJurisdictionalAmount: false,
        showCourtJurisdiction: true,
        showIncidentZip: false,
        showAdditionalCaseInfo: false,
    },
    // Juvenile Dependency
    '14': {
        showJurisdictionalAmount: false,
        showCourtJurisdiction: true,
        showIncidentZip: false,
        showAdditionalCaseInfo: false,
    },
    // Probate
    '4': {
        showJurisdictionalAmount: false,
        showCourtJurisdiction: true,
        showIncidentZip: false,
        showAdditionalCaseInfo: false,
    },
    // Probate or Mental Health
    '13': {
        showJurisdictionalAmount: false,
        showCourtJurisdiction: true,
        showIncidentZip: false,
        showAdditionalCaseInfo: false,
    },
};
/**
 * Per-category field visibility for Madera County (JTI baseline sandbox).
 *
 * Key = JTI CASE_TYPE code (which maps to our "Case Category" after taxonomy inversion).
 */
export const MADERA_CATEGORY_FIELDS = {
    // Family Law/Support
    '211110': {
        showJurisdictionalAmount: false,
        showCourtJurisdiction: true,
        showIncidentZip: true,
        showAdditionalCaseInfo: true,
    },
    // Civil Unlimited
    '411110': {
        showJurisdictionalAmount: true,
        showCourtJurisdiction: false,
        showIncidentZip: true,
        showAdditionalCaseInfo: true,
    },
    // Civil Limited
    '421110': {
        showJurisdictionalAmount: true,
        showCourtJurisdiction: false,
        showIncidentZip: true,
        showAdditionalCaseInfo: true,
    },
    // Probate
    '511110': {
        showJurisdictionalAmount: false,
        showCourtJurisdiction: true,
        showIncidentZip: true,
        showAdditionalCaseInfo: true,
    },
    // Small Claims
    '711110': {
        showJurisdictionalAmount: true,
        showCourtJurisdiction: false,
        showIncidentZip: true,
        showAdditionalCaseInfo: false,
    },
};
/**
 * Get field visibility flags for a case category.
 * Checks both LA County codes (e.g., "CU") and Madera codes (e.g., "411110").
 * Returns safe defaults for unknown category codes.
 */
export function getCaseCategoryFieldVisibility(categoryEfmCode) {
    return LA_COUNTY_CATEGORY_FIELDS[categoryEfmCode]
        ?? MADERA_CATEGORY_FIELDS[categoryEfmCode]
        ?? DEFAULT_VISIBILITY;
}
